import React, { ReactNode, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { PASSWORD_RULES, meetsPasswordPolicy } from '../../utils/password_rules';

/**
 * SHER-803 - what an invitee sees after opening their invite link.
 *
 * Presentational only: the page that hosts it reads the link, asks the API for the link's state,
 * and turns that into `view`. Every secret stays in that page; nothing secret is a prop here.
 */
export type AcceptInvitationView =
    | 'loading'
    | 'valid_new'
    | 'valid_existing'
    | 'expired'
    | 'used'
    | 'invalid'
    | 'resend_requested'
    | 'success'
    // Joined from an invitation-only session (a dataset consumer account): nothing to continue to.
    | 'done_close'
    | 'error';

export interface NewAccountValues {
    name: string;
    surnames: string;
    password: string;
}

export interface AcceptInvitationProps {
    view: AcceptInvitationView;
    investigationName?: string;
    inviterName?: string;
    email?: string;
    /** An action is in flight; its button is disabled so it cannot fire twice. */
    busy?: boolean;
    /** The CAPTCHA widget the registration endpoint requires, supplied by the host page. */
    captcha?: ReactNode;
    onRegister?: (values: NewAccountValues) => void;
    onAccept?: () => void;
    onRequestResend?: () => void;
    onContinue?: () => void;
}

function Heading({ investigationName, inviterName }: Pick<AcceptInvitationProps, 'investigationName' | 'inviterName'>) {
    if (!investigationName) {
        return null;
    }
    return (
        <Box mb={2}>
            <Typography variant="body2" color="text.secondary"><Translate id="invitation.accept.title" /></Typography>
            <Typography variant="h5" component="h1">{investigationName}</Typography>
            {inviterName && (
                <Typography variant="body2" color="text.secondary">
                    <Translate id="invitation.accept.invited_by" /> {inviterName}
                </Typography>
            )}
        </Box>
    );
}

function Notice({ severity, title, body }: { severity: 'warning' | 'error' | 'info' | 'success'; title: string; body?: string }) {
    return (
        <Alert severity={severity}>
            <Typography fontWeight={600}><Translate id={title} /></Typography>
            {body && <Typography variant="body2"><Translate id={body} /></Typography>}
        </Alert>
    );
}

function NewAccountForm({ email, busy, captcha, onRegister }: Pick<AcceptInvitationProps, 'email' | 'busy' | 'captcha' | 'onRegister'>) {
    const [name, setName] = useState('');
    const [surnames, setSurnames] = useState('');
    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    const mismatch = repeat.length > 0 && repeat !== password;
    const ready = name.trim() !== '' && surnames.trim() !== '' && meetsPasswordPolicy(password) && repeat === password;

    return (
        <Stack spacing={2}>
            <Typography variant="body2">
                <Translate id="invitation.accept.new_account_intro" /> <strong>{email}</strong>
            </Typography>
            <TextField id="invitation-name" label={<Translate id="invitation.accept.name" />} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
            <TextField id="invitation-surnames" label={<Translate id="invitation.accept.surnames" />} value={surnames} onChange={(e) => setSurnames(e.target.value)} size="small" fullWidth />
            <TextField id="invitation-password" type="password" autoComplete="new-password" label={<Translate id="invitation.accept.password" />} value={password} onChange={(e) => setPassword(e.target.value)} size="small" fullWidth />
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {PASSWORD_RULES.map((rule) => (
                    <Typography component="li" variant="caption" key={rule.key} color={rule.test(password) ? 'success.main' : 'text.secondary'}>
                        <Translate id={rule.translationId} />
                    </Typography>
                ))}
            </Box>
            <TextField
                id="invitation-repeat-password"
                type="password"
                autoComplete="new-password"
                label={<Translate id="invitation.accept.repeat_password" />}
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                error={mismatch}
                helperText={mismatch ? <Translate id="invitation.accept.passwords_differ" /> : undefined}
                size="small"
                fullWidth
            />
            {captcha}
            <Button variant="contained" disabled={!ready || busy} onClick={() => onRegister && onRegister({ name: name.trim(), surnames: surnames.trim(), password })}>
                <Translate id="invitation.accept.create_and_join" />
            </Button>
        </Stack>
    );
}

export default function AcceptInvitation(props: AcceptInvitationProps) {
    const { view, busy } = props;

    let body: ReactNode;
    switch (view) {
        case 'loading':
            body = (
                <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography><Translate id="invitation.accept.loading" /></Typography>
                </Stack>
            );
            break;
        case 'valid_new':
            body = <NewAccountForm email={props.email} busy={busy} captcha={props.captcha} onRegister={props.onRegister} />;
            break;
        case 'valid_existing':
            body = (
                <Stack spacing={2}>
                    <Typography variant="body2"><Translate id="invitation.accept.existing_intro" /></Typography>
                    <Button variant="contained" disabled={busy} onClick={props.onAccept}><Translate id="invitation.accept.accept" /></Button>
                </Stack>
            );
            break;
        case 'expired':
        case 'used':
            body = (
                <Stack spacing={2}>
                    <Notice severity="warning" title={`invitation.accept.${view}_title`} body={`invitation.accept.${view}_body`} />
                    <Button variant="outlined" disabled={busy} onClick={props.onRequestResend}><Translate id="invitation.accept.request_resend" /></Button>
                </Stack>
            );
            break;
        case 'invalid':
            body = <Notice severity="error" title="invitation.accept.invalid_title" body="invitation.accept.invalid_body" />;
            break;
        case 'resend_requested':
            body = <Notice severity="info" title="invitation.accept.resend_requested_title" body="invitation.accept.resend_requested_body" />;
            break;
        case 'success':
            body = (
                <Stack spacing={2}>
                    <Notice severity="success" title="invitation.accept.success_title" />
                    <Button variant="contained" onClick={props.onContinue}><Translate id="invitation.accept.continue" /></Button>
                </Stack>
            );
            break;
        case 'done_close':
            body = <Notice severity="success" title="invitation.accept.success_title" body="invitation.accept.done_close_body" />;
            break;
        default:
            body = <Notice severity="error" title="invitation.accept.error_title" body="invitation.accept.error_body" />;
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Heading investigationName={props.investigationName} inviterName={props.inviterName} />
                {body}
            </CardContent>
        </Card>
    );
}
